<script setup lang="ts">
import type { AdvisorEngagementBodyType, CryptoInfo } from '@forgd/contract/types'
import type { FieldErrorItem } from '@forgd/ui-kit/blocks/field'
import { useDiscardGuard } from '#core/composables/useDiscardGuard'
import { bindField, revealErrors } from '#core/utils/bind-field'
import { AdvisorEngagementBody } from '@forgd/contract/types'
import { BaseLayerOptions, SectorOptions } from '@forgd/shared/dropdowns'
import { BaseLayerTypeEnumValues, SectorTypeEnumValues } from '@forgd/shared/enums'
import { Alert, AlertDescription, AlertTitle } from '@forgd/ui-kit/alert'
import { Avatar, AvatarInitials } from '@forgd/ui-kit/avatar'
import { DateInput } from '@forgd/ui-kit/blocks/date-input'
import { NoSelection } from '@forgd/ui-kit/blocks/empty-states'
import { Field, FieldContent, FieldError, FieldLabel } from '@forgd/ui-kit/blocks/field'
import { Button } from '@forgd/ui-kit/button'
import { Card, CardContent, CardHeader, CardTitle } from '@forgd/ui-kit/card'
import { Input } from '@forgd/ui-kit/input'
import { Select, SelectContent, SelectItem, SelectItemText, SelectTrigger, SelectValue } from '@forgd/ui-kit/select'
import { Sheet, SheetBody, SheetClose, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle } from '@forgd/ui-kit/sheet'
import { ToggleGroup, ToggleGroupItem } from '@forgd/ui-kit/toggle-group'
import { InformationCircleIcon, XMarkIcon } from '@heroicons/vue/24/outline'
import { useMutation, useQuery } from '@pinia/colada'
import { isEqual } from 'lodash-es'
import { revalidateLogic, useForm } from '@tanstack/vue-form'
import { withHttps } from 'ufo'
import { z } from 'zod'

const props = defineProps<{
  isOpen: boolean
  advisorOrganizationId: string
}>()

const emit = defineEmits<{
  (e: 'success'): void
  (e: 'update:isOpen', value: boolean): void
}>()

const formSchema = z.object({
  advisorOrganizationId: z.string(),
  advisorEditorIds: z.array(z.string()),
  coingeckoId: z.string().nullable(),
  isEngagedAsAdvisor: z.boolean(),
  tokenListed: z.boolean(),
  image: z.string(),
  name: z.string(),
  ticker: z.string(),
  tgeDate: z.string(),
  link: z.string(),
  sector: z.array(z.enum(SectorTypeEnumValues)),
  baseLayer: z.array(z.enum(BaseLayerTypeEnumValues)),
}).pipe(AdvisorEngagementBody)

type EngagementFormValues = z.input<typeof formSchema>

const toast = useAppToast()
const { getCoingeckoUrl } = useTokenomicsUrl()

const isCreating = ref<boolean>(false)
const coingeckoUrlId = useId()

function defaultValues(overrides: Partial<EngagementFormValues> = {}): EngagementFormValues {
  return {
    advisorOrganizationId: props.advisorOrganizationId,
    advisorEditorIds: [],
    coingeckoId: null,
    isEngagedAsAdvisor: true,
    tokenListed: false,
    image: '',
    name: '',
    ticker: '',
    tgeDate: '',
    link: '',
    sector: [],
    baseLayer: [],
    ...overrides,
  }
}

const form = useForm({
  defaultValues: defaultValues(),
  validationLogic: revalidateLogic({ mode: 'change', modeAfterSubmission: 'change' }),
  validators: { onDynamic: formSchema },
  onSubmitInvalid: ({ formApi }) => {
    revealErrors(formApi)
    toast.error({
      title: 'All fields are mandatory',
      description: 'Please ensure all fields have data or selections made, and try again.',
    })
  },
  onSubmit: ({ value }) => createEngagement(formSchema.parse(value)),
})

const tokenListed = form.useSelector(s => s.values.tokenListed)
const coingeckoId = form.useSelector(s => s.values.coingeckoId)
const tokenStatus = computed(() => tokenListed.value ? 'listed' : 'pre-tge')
const isDirty = form.useSelector(s => !isEqual(s.values, defaultValues({ tokenListed: s.values.tokenListed })))

const { confirmOpen: discardOpen, guard: guardClose, confirm: discardChanges } = useDiscardGuard(isDirty, () => {
  emit('update:isOpen', false)
})

watch(() => props.isOpen, (open) => {
  if (open) {
    form.reset(defaultValues())
  }
})

const { data: teammates } = useQuery({
  ...buildApiQuery('/membership/{organizationId}/get-members', {
    params: () => ({
      path: {
        organizationId: props.advisorOrganizationId,
      },
    }),
  }),
})

const { mutateAsync: createEngagementAsync } = useMutation(buildApiMutation('/advisor/engagements', {
  method: 'post',
}))

const activeTeammates = computed(() => {
  if (teammates.value) {
    return teammates.value.members
      .filter((m): m is typeof m & { userId: string } => m.status === 'active' && m.userId !== null)
      .map(m => ({
        id: m.userId,
        firstName: m.firstName,
        lastName: m.lastName,
        email: m.email ?? undefined,
        name: ([m.firstName, m.lastName].filter(Boolean).join(' ')) || m.email || '',
      }))
  }
  return []
})

async function createEngagement(body: AdvisorEngagementBodyType) {
  try {
    isCreating.value = true
    await Promise.all([
      createEngagementAsync({ body }),
      // Make sure that the processing state is visible for 3 seconds
      new Promise(resolve => setTimeout(resolve, 3000)),
    ])

    toast.success({
      title: 'Engagement added successfully',
    })

    emit('update:isOpen', false)
    emit('success')
  }
  catch {
    toast.error({
      title: 'Something went wrong!',
      description: 'We were not able to add the engagement due to an internal error. Please try again.',
    })
  }
  finally {
    isCreating.value = false
  }
}

function normalizeLink(value: string) {
  const url = value.trim()
  return url ? withHttps(url) : ''
}

function onCoingeckoChange(coin: CryptoInfo) {
  toast.clear()
  const baseLayerOption = coin.baseLayerId
    ? BaseLayerOptions.find(b => b.coingeckoId === coin.baseLayerId || b.value === coin.baseLayerId)
    : undefined
  const baseLayer = z.enum(BaseLayerTypeEnumValues).safeParse(baseLayerOption?.value)
  form.reset(defaultValues({
    tokenListed: true,
    coingeckoId: coin.id,
    image: coin.image,
    name: coin.name,
    ticker: coin.symbol.toUpperCase(),
    tgeDate: coin.tgeDate ?? '',
    link: coin.homepage ?? '',
    baseLayer: baseLayer.success ? [baseLayer.data] : [],
  }))
}

function onTokenStatusChange(value: unknown) {
  if (value !== 'listed' && value !== 'pre-tge') {
    return
  }
  toast.clear()
  form.reset(defaultValues({ tokenListed: value === 'listed' }))
}

function coinSelectErrors(errors: FieldErrorItem[]): FieldErrorItem[] {
  return errors.length ? ['Search and select a listed project'] : []
}

function advisorErrors(errors: FieldErrorItem[]): FieldErrorItem[] {
  return errors.length ? ['Select at least one advisor'] : []
}
</script>

<template>
  <Sheet :open="isOpen" :modal="false" @update:open="(open) => !open && emit('update:isOpen', false)">
    <SheetContent side="right" size="sm" @interact-outside="guardClose" @escape-key-down="guardClose">
      <SheetHeader>
        <div>
          <SheetTitle>Add a new engagement</SheetTitle>
          <SheetDescription>Complete all following details to continue.</SheetDescription>
        </div>
        <SheetClose as-child>
          <Button type="button" size="sm" variant="ghost" icon-only aria-label="Close" data-testid="close-slideover-button">
            <XMarkIcon aria-hidden="true" />
          </Button>
        </SheetClose>
      </SheetHeader>
      <SheetBody>
        <EngagementProcessing v-if="isCreating" />
        <form v-else id="create-engagement-form" novalidate class="flex flex-col gap-5" @submit.prevent="form.handleSubmit()">
          <Card>
            <CardHeader>
              <CardTitle>Token Status</CardTitle>
            </CardHeader>
            <CardContent class="flex flex-col gap-4">
              <ToggleGroup
                preset="dark"
                layout="fill"
                size="lg"
                :model-value="tokenStatus"
                @update:model-value="onTokenStatusChange"
              >
                <ToggleGroupItem value="pre-tge">
                  Pre-TGE
                </ToggleGroupItem>
                <ToggleGroupItem value="listed">
                  Listed
                </ToggleGroupItem>
              </ToggleGroup>
              <form.Field v-if="tokenListed" v-slot="{ field }" name="coingeckoId">
                <Field>
                  <FieldContent>
                    <CoinSelect
                      data-testid="coin-select"
                      :coingecko-id="field.state.value"
                      @change="onCoingeckoChange"
                    />
                    <FieldError :id="bindField(field).error.id" :errors="coinSelectErrors(bindField(field).error.errors)" />
                  </FieldContent>
                </Field>
              </form.Field>
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <CardTitle>Project details</CardTitle>
            </CardHeader>
            <CardContent>
              <NoSelection
                v-if="tokenListed && !coingeckoId"
                class="h-62.5"
                title="Search and Select the listed project above"
                message="Project details will appear here once selected."
              />
              <div v-else class="grid gap-5 sm:grid-cols-2">
                <Field v-if="tokenListed" class="sm:col-span-2">
                  <FieldLabel :for="coingeckoUrlId">
                    CoinGecko URL
                  </FieldLabel>
                  <FieldContent>
                    <Input :id="coingeckoUrlId" :model-value="getCoingeckoUrl(coingeckoId)" readonly />
                  </FieldContent>
                </Field>
                <form.Field v-slot="{ field }" name="name">
                  <Field>
                    <FieldLabel v-bind="bindField(field).label">
                      Token name
                    </FieldLabel>
                    <FieldContent>
                      <Input v-bind="bindField(field).control" :name="field.name" placeholder="e.g. Abeezee" />
                      <FieldError v-bind="bindField(field).error" />
                    </FieldContent>
                  </Field>
                </form.Field>
                <form.Field v-slot="{ field }" name="ticker">
                  <Field>
                    <FieldLabel v-bind="bindField(field).label">
                      Token ticker
                    </FieldLabel>
                    <FieldContent>
                      <Input v-bind="bindField(field).control" :name="field.name" placeholder="e.g. ABC" />
                      <FieldError v-bind="bindField(field).error" />
                    </FieldContent>
                  </Field>
                </form.Field>
                <form.Field v-slot="{ field }" name="sector">
                  <Field>
                    <FieldLabel v-bind="bindField(field).label">
                      Sector
                    </FieldLabel>
                    <FieldContent>
                      <Select
                        :model-value="field.state.value"
                        multiple
                        @update:model-value="bindField(field).control['onUpdate:modelValue']"
                        @update:open="(open) => !open && field.handleBlur()"
                      >
                        <SelectTrigger
                          :id="bindField(field).control.id"
                          :error="bindField(field).control.error"
                          :aria-describedby="bindField(field).control['aria-describedby']"
                          data-testid="sector"
                        >
                          <SelectValue placeholder="Choose at least one" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem v-for="opt in SectorOptions" :key="opt.value" :value="opt.value">
                            {{ opt.label }}
                          </SelectItem>
                        </SelectContent>
                      </Select>
                      <FieldError v-bind="bindField(field).error" />
                    </FieldContent>
                  </Field>
                </form.Field>
                <form.Field v-slot="{ field }" name="link">
                  <Field>
                    <FieldLabel v-bind="bindField(field).label">
                      Website
                    </FieldLabel>
                    <FieldContent>
                      <Input
                        v-bind="bindField(field).control"
                        :name="field.name"
                        placeholder="www.yourproject.com"
                        @change="field.handleChange(normalizeLink(field.state.value))"
                      />
                      <FieldError v-bind="bindField(field).error" />
                    </FieldContent>
                  </Field>
                </form.Field>
                <form.Field v-slot="{ field }" name="baseLayer">
                  <Field>
                    <FieldLabel v-bind="bindField(field).label">
                      Base layer
                    </FieldLabel>
                    <FieldContent>
                      <Select
                        :model-value="field.state.value"
                        multiple
                        @update:model-value="bindField(field).control['onUpdate:modelValue']"
                        @update:open="(open) => !open && field.handleBlur()"
                      >
                        <SelectTrigger
                          :id="bindField(field).control.id"
                          :error="bindField(field).control.error"
                          :aria-describedby="bindField(field).control['aria-describedby']"
                          data-testid="base-layer"
                        >
                          <SelectValue placeholder="Choose at least one" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem v-for="opt in BaseLayerOptions" :key="opt.value" :value="opt.value">
                            {{ opt.label }}
                          </SelectItem>
                        </SelectContent>
                      </Select>
                      <FieldError v-bind="bindField(field).error" />
                    </FieldContent>
                  </Field>
                </form.Field>
                <form.Field v-slot="{ field }" name="tgeDate">
                  <Field>
                    <FieldLabel v-bind="bindField(field).label">
                      Estimated date of TGE
                    </FieldLabel>
                    <FieldContent>
                      <DateInput
                        :id="bindField(field).control.id"
                        :model-value="field.state.value"
                        :error="bindField(field).control.error"
                        :aria-describedby="bindField(field).control['aria-describedby']"
                        data-testid="tge-date"
                        @update:model-value="field.handleChange($event ?? '')"
                        @focusout="field.handleBlur()"
                      />
                      <FieldError :id="bindField(field).error.id" :errors="bindField(field).error.errors.slice(0, 1)" />
                    </FieldContent>
                  </Field>
                </form.Field>
                <form.Field v-slot="{ field }" name="image">
                  <Field class="sm:col-span-2">
                    <FieldContent>
                      <ProjectImageUpload
                        :image="field.state.value || ''"
                        class="w-full"
                        no-project
                        @update:image="field.handleChange($event)"
                      >
                        <template #label>
                          Select image file or drop it here
                        </template>
                        <template #description>
                          Upload your project's image
                        </template>
                      </ProjectImageUpload>
                      <FieldError v-bind="bindField(field).error" />
                    </FieldContent>
                  </Field>
                </form.Field>
                <form.Field v-slot="{ field }" name="isEngagedAsAdvisor">
                  <div class="flex flex-wrap items-center justify-between gap-3 rounded-lg bg-neutral-600/30 p-4 sm:col-span-2">
                    <div class="text-sm text-brand-900">
                      I am engaged with this project as an advisor
                    </div>
                    <ToggleGroup
                      preset="dark"
                      layout="fit"
                      size="sm"
                      aria-label="I am engaged with this project as an advisor"
                      :model-value="field.state.value ? 'yes' : 'no'"
                      @update:model-value="(value) => (value === 'yes' || value === 'no') && field.handleChange(value === 'yes')"
                    >
                      <ToggleGroupItem value="no">
                        No
                      </ToggleGroupItem>
                      <ToggleGroupItem value="yes" color="emerald">
                        Yes
                      </ToggleGroupItem>
                    </ToggleGroup>
                  </div>
                </form.Field>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <CardTitle>Assign an advisor</CardTitle>
            </CardHeader>
            <CardContent class="flex flex-col gap-4">
              <form.Field v-slot="{ field }" name="advisorEditorIds">
                <Field>
                  <FieldContent>
                    <Select
                      :model-value="field.state.value"
                      multiple
                      @update:model-value="bindField(field).control['onUpdate:modelValue']"
                      @update:open="(open) => !open && field.handleBlur()"
                    >
                      <SelectTrigger
                        :id="bindField(field).control.id"
                        :error="bindField(field).control.error"
                        :aria-describedby="bindField(field).control['aria-describedby']"
                        aria-label="Assigned advisors"
                        data-testid="advisor-select"
                      >
                        <SelectValue placeholder="Select advisors" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem
                          v-for="(option, index) in activeTeammates"
                          :key="option.id"
                          :value="option.id"
                          :data-testid="`advisor-select-option-${index}`"
                          composed
                        >
                          <Avatar size="sm" aria-hidden="true">
                            <AvatarInitials :first-name="option.firstName" :last-name="option.lastName" />
                          </Avatar>
                          <SelectItemText class="truncate text-xs text-secondary">
                            {{ option.name }}
                          </SelectItemText>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                    <FieldError :id="bindField(field).error.id" :errors="advisorErrors(bindField(field).error.errors)" />
                  </FieldContent>
                </Field>
              </form.Field>
              <Alert>
                <InformationCircleIcon />
                <AlertTitle>Why do you need to assign an advisor?</AlertTitle>
                <AlertDescription>This assigned advisor will be the only teammate who can edit the project details. All other teammates can access it in view-only mode.</AlertDescription>
              </Alert>
            </CardContent>
          </Card>
        </form>
      </SheetBody>
      <SheetFooter>
        <Button type="button" variant="outline" class="flex-1" :disabled="isCreating" @click="emit('update:isOpen', false)">
          Cancel
        </Button>
        <Button type="submit" form="create-engagement-form" class="flex-1" :disabled="isCreating">
          Add Engagement
        </Button>
      </SheetFooter>
    </SheetContent>
  </Sheet>
  <DiscardChangesDialog v-model:open="discardOpen" @confirm="discardChanges" />
</template>
